import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Chip,
  Avatar
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Save as SaveIcon,
  Key as KeyIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { UserUpdate } from '../types/models';

const Profile: React.FC = () => {
  const { user, isAdmin, isBuyer, isSeller, refreshUser } = useAuth();
  
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone_number: user?.phone_number || '',
    government_id: user?.government_id || '',
    password: '',
    confirmPassword: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset states
    setLoading(true);
    setSuccess(false);
    setError(null);
    
    // Validate passwords match if changing password
    if (formData.password && formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }
    
    try {
      // Prepare update data
      const updateData: UserUpdate = {
        full_name: formData.full_name
      };
      
      // Only include password if it's being changed
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      // Only include email if it's different from current
      if (formData.email !== user?.email) {
        updateData.email = formData.email;
      }
      
      // Only include phone_number if it's different from current
      if (formData.phone_number !== user?.phone_number) {
        updateData.phone_number = formData.phone_number;
      }
      
      // Only include government_id if it's different from current
      if (formData.government_id !== user?.government_id) {
        updateData.government_id = formData.government_id;
      }
      
      // Update user profile
      await apiService.put('/users/me', updateData);
      
      // Refresh user data to get the updated information
      await refreshUser();
      
      // Show success message
      setSuccess(true);
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));
      
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.detail || 'Error al actualizar el perfil. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };
  
  // Generate user role chips
  const getRoleChips = () => {
    const roles = [];
    
    if (isAdmin) roles.push(<Chip key="admin" label="Administrador" color="error" size="small" sx={{ mr: 1 }} />);
    if (isSeller) roles.push(<Chip key="seller" label="Vendedor" color="primary" size="small" sx={{ mr: 1 }} />);
    if (isBuyer) roles.push(<Chip key="buyer" label="Comprador" color="success" size="small" />);
    
    return roles;
  };
  
  if (!user) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          Información del usuario no disponible. Por favor inicia sesión nuevamente.
        </Alert>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
          Panel de Control
        </Link>
        <Typography color="text.primary">Mi Perfil</Typography>
      </Breadcrumbs>
      
      <Typography variant="h4" gutterBottom>
        Mi Perfil
      </Typography>
      
      {/* User Info Card */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ width: 80, height: 80, mr: 2, bgcolor: 'primary.main' }}>
            {user.full_name.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h5">{user.full_name}</Typography>
            <Typography variant="body1" color="text.secondary">{user.email}</Typography>
            <Box sx={{ mt: 1 }}>
              {getRoleChips()}
            </Box>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Número de Teléfono
            </Typography>
            <Typography variant="body1">
              {user.phone_number || 'No proporcionado'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Cédula de Identidad
            </Typography>
            <Typography variant="body1">
              {user.government_id || 'No proporcionado'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Edit Profile Form */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Editar Perfil
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            ¡Perfil actualizado exitosamente!
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre Completo"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Correo Electrónico"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Número de Teléfono"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cédula de Identidad"
                name="government_id"
                value={formData.government_id}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <BadgeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nueva Contraseña"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                helperText="Dejar en blanco para mantener la contraseña actual"
                InputProps={{
                  startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Confirmar Nueva Contraseña"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={!formData.password}
                InputProps={{
                  startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default Profile;
